import * as React from 'react'
import {useEffect} from 'react'
import {graphql, useFragment, usePreloadedQuery, useQueryLoader} from 'react-relay'
import type {PreloadedQuery} from 'react-relay/relay-hooks/EntryPointTypes'

import {removeAgent} from '../../../actions/fetch'
import {useAppDispatch} from '../../../hooks/react-redux'
import {useOnDemandQuery} from '../../../hooks/relay/useOnDemandQuery'
import {useRefetchableLazyLoadQuery} from '../../../hooks/relay/useRefetchableLazyLoadQuery'
import {extractErrorFromRelay} from '../../../relay/extractErrorFromRelay'
import {HTTPCodesEnum} from '../../../rest/errors/HTTPError'
import {getAgentsOverviewHref, getFavoriteAgentPoolsHref} from '../../../routes'
import type {AgentId} from '../../../types'
import {internalProps} from '../../../types/BS_types'
import {keyValue} from '../../../utils/object'
import {subscribeOnAgentEvents, subscribeOnOverallEvents} from '../../../utils/subscriber'
import * as SubscriptionEvents from '../../../utils/subscriptionEvents'

import type {AgentsPagesAgentExistsQuery} from './__generated__/AgentsPagesAgentExistsQuery.graphql'
import type {AgentsPagesAgentTypesQuery} from './__generated__/AgentsPagesAgentTypesQuery.graphql'
import type {AgentsPagesCanViewAgentDetailsFragment$key} from './__generated__/AgentsPagesCanViewAgentDetailsFragment.graphql'
import {useAgentPoolFavoriteList} from './AgentPoolsFavoritePage/hooks/useAgentPoolFavorite'
import {markAgentDeleted, REGULAR_OVERALL_AGENT_EVENT_TOPICS} from './AgentsPages.actions'
import type {AgentsTreeExpandStateKey} from './AgentsPages.effects'
import {getStoredAgentsTreeExpandState, setStoredExpandState} from './AgentsPages.effects'
import {agentTypesQuery, NativeAgentTypesQuery} from './AgentsPages.queries'
import {getAgentPoolsFromNative} from './AgentsPages.selectors'
import type {AgentBranch, AgentBranchId} from './AgentsSidebar/AgentsSidebar.types'

export function useStoredAgentsTreeBranchState(
  key: AgentsTreeExpandStateKey,
  type: AgentBranch,
  id?: AgentBranchId,
  defaultValue = true,
): [boolean, () => void] {
  const expandState = getStoredAgentsTreeExpandState(key)
  const expandStateForType = expandState[type]

  const wasExpanded = expandStateForType != null ? expandStateForType[id!] : false
  const [expanded, setExpanded] = React.useState(
    wasExpanded !== undefined ? wasExpanded : defaultValue,
  )

  const toggleExpanded = React.useCallback(() => {
    if (id != null) {
      setStoredExpandState(key, keyValue(type, keyValue(id, !expanded)))
    }
    setExpanded(!expanded)
  }, [key, type, id, expanded])

  return [expanded, toggleExpanded]
}

export function useSubscribeOnAgentTypes(
  initialQueryRef: PreloadedQuery<AgentsPagesAgentTypesQuery>,
) {
  const [queryReference, loadQuery] = useQueryLoader<AgentsPagesAgentTypesQuery>(
    agentTypesQuery,
    initialQueryRef,
  )

  useEffect(() => {
    let initial = true

    return subscribeOnOverallEvents(REGULAR_OVERALL_AGENT_EVENT_TOPICS, () => {
      if (!initial) {
        loadQuery(
          {},
          {fetchPolicy: 'store-and-network', networkCacheConfig: {metadata: {isRefetch: true}}},
        )
      }
      initial = false
    })
  }, [loadQuery])

  return queryReference
}

export function useNativeSubscribeOnAgentTypes(skip: boolean) {
  const [nativeAgentTypesQueryResponse, refetch] = useRefetchableLazyLoadQuery(
    NativeAgentTypesQuery,
    {skip},
  )

  useEffect(() => {
    if (skip) {
      return () => undefined
    }

    let initial = true

    return subscribeOnOverallEvents(REGULAR_OVERALL_AGENT_EVENT_TOPICS, () => {
      if (!initial) {
        refetch()
      }
      initial = false
    })
  }, [refetch, skip])

  if (skip || nativeAgentTypesQueryResponse == null) {
    return undefined
  }

  return getAgentPoolsFromNative({nativeAgentTypesQueryResponse})
}

const agentExistsQuery = graphql`
  query AgentsPagesAgentExistsQuery($locator: String!) {
    agentRest(agentLocator: $locator) {
      id
    }
  }
`
export function useSubscribeOnAgentRemoval(agentId: AgentId, preserveAfterDelete = false) {
  const fetchAgent = useOnDemandQuery<AgentsPagesAgentExistsQuery>(agentExistsQuery)
  const dispatch = useAppDispatch()
  useEffect(
    () =>
      subscribeOnAgentEvents(agentId, [SubscriptionEvents.AGENT_REMOVED], () =>
        fetchAgent({locator: `id:${agentId}`}, true)
          .toPromise()
          .catch(originalError => {
            const error = extractErrorFromRelay(originalError)
            if (
              error != null &&
              'statusCode' in error &&
              error.statusCode === HTTPCodesEnum.NOT_FOUND
            ) {
              dispatch(preserveAfterDelete ? markAgentDeleted(agentId) : removeAgent(agentId))
            }
          }),
      ),
    [agentId, fetchAgent, dispatch, preserveAfterDelete],
  )
}

const defaultLimit = 20
const limit = internalProps['teamcity.ui.agentPoolsThresholdForRedirect'] ?? defaultLimit

export const useRedirectAgentPageUrl = (queryRef: PreloadedQuery<AgentsPagesAgentTypesQuery>) => {
  const agentTypesQueryResponse = usePreloadedQuery(agentTypesQuery, queryRef)
  const favoriteAgentPoolsExist = useAgentPoolFavoriteList().length > 0

  // Clicking on the Agents tab in the header leads to:
  // 1. If there are favorite pools. Display them when opening all agents.
  // 2. If there are no favorite pools and the total number of pools is less than N. Show the Overview.
  // 3. If there are no favorite pools and the total number of pools is more than N. Show the placeholder.
  const poolsCount = agentTypesQueryResponse.agentPoolsRest?.agentPool?.length ?? 0
  return favoriteAgentPoolsExist || poolsCount > limit
    ? getFavoriteAgentPoolsHref()
    : getAgentsOverviewHref()
}

// If there's "view agent details" permission, the API returns the full list of agent pools.
// The same happens if there's "view project agent details" permission, for any project (which apparently is a bug).
// If there are no above permissions, the API returns empty list of agent pools.
// It seems safe to use this fact to detect the lack of permissions.
const canViewAgentDetailsFragment = graphql`
  fragment AgentsPagesCanViewAgentDetailsFragment on Query {
    agentPoolsRest {
      count
    }
  }
`

export function useCanViewAgentDetails(key: AgentsPagesCanViewAgentDetailsFragment$key | null) {
  const {agentPoolsRest} = useFragment(canViewAgentDetailsFragment, key) ?? {}
  return Boolean(agentPoolsRest?.count)
}
